import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Search,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useProductData } from '../data'
import type {
  JdAdvertisingCampaignPerformanceSummary,
  JdAdvertisingCampaignPlanPerformance,
  JdAdvertisingOrderDailyRecord,
  JdAdvertisingOrderDetailResponse,
  JdAdvertisingOrderPlanSummary,
  JdAdvertisingOrderSkuSummary,
  ProductRecord,
} from '../types'

const currency = new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY', minimumFractionDigits: 2 })
const integer = new Intl.NumberFormat('zh-CN')
const decimal = new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const shortDate = (value: string) => value.replaceAll('-', '/')
const money = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100
const ratio = (top: number, bottom: number) => bottom ? top / bottom : 0

type MappingFilter = 'all' | 'mapped' | 'unmapped'
type RangeKey = 'yesterday' | 'last7' | 'last30' | 'day' | 'week' | 'month' | 'custom'
type SortKey = 'spend' | 'spendShare' | 'amount' | 'amountShare' | 'roi' | 'cpc' | 'conversion' | 'orderCost'
type SortDirection = 'asc' | 'desc'

interface DateRange { key: RangeKey; start: string; end: string }
interface AdvertisingSkuRow {
  summary: JdAdvertisingOrderSkuSummary
  product: ProductRecord | null
  productCode: string
  productName: string
  mapped: boolean
}

const sortLabels: Record<SortKey, string> = {
  spend: '花费', spendShare: '消耗占比', amount: '归因成交金额', amountShare: '成交贡献占比',
  roi: 'ROI', cpc: 'CPC', conversion: '转化率', orderCost: '订单成本',
}

function isoDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function parseDate(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function addDays(value: string, days: number) {
  const date = parseDate(value)
  date.setDate(date.getDate() + days)
  return isoDate(date)
}

function spanDays(start: string, end: string) {
  if (!start || !end) return 0
  return Math.round((parseDate(end).getTime() - parseDate(start).getTime()) / 86400000)
}

function rangeLabel(range: Pick<DateRange, 'start' | 'end'>) {
  return range.start === range.end ? shortDate(range.start) : `${shortDate(range.start)}–${shortDate(range.end)}`
}

function mondayOf(value: string) {
  const date = parseDate(value)
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7))
  return isoDate(date)
}

function presetRange(key: Exclude<RangeKey, 'custom'>, latestDate: string): DateRange {
  if (key === 'last7') return { key, start: addDays(latestDate, -6), end: latestDate }
  if (key === 'last30') return { key, start: addDays(latestDate, -29), end: latestDate }
  if (key === 'week') return { key, start: mondayOf(latestDate), end: latestDate }
  if (key === 'month') return { key, start: `${latestDate.slice(0, 7)}-01`, end: latestDate }
  return { key, start: latestDate, end: latestDate }
}

function campaignSummary(values: Omit<JdAdvertisingCampaignPerformanceSummary, 'clickThroughRate' | 'averageClickCost' | 'averageOrderCost' | 'roi'>) {
  return {
    ...values,
    adSpend: money(values.adSpend),
    directOrderAmount: money(values.directOrderAmount),
    indirectOrderAmount: money(values.indirectOrderAmount),
    totalOrderAmount: money(values.totalOrderAmount),
    clickThroughRate: values.impressions ? values.clicks / values.impressions * 100 : 0,
    averageClickCost: values.clicks ? money(values.adSpend / values.clicks) : 0,
    averageOrderCost: values.totalOrderLines ? money(values.adSpend / values.totalOrderLines) : null,
    roi: values.adSpend ? values.totalOrderAmount / values.adSpend : 0,
  }
}

function aggregateCampaigns(records: JdAdvertisingOrderDailyRecord[]) {
  const byId = new Map<string, JdAdvertisingCampaignPlanPerformance>()
  for (const record of records) {
    for (const plan of record.campaignPerformance?.plans || []) {
      const current = byId.get(plan.campaignId)
      const base = current || plan
      const summed = campaignSummary({
        impressions: (current?.impressions || 0) + plan.impressions,
        clicks: (current?.clicks || 0) + plan.clicks,
        adSpend: (current?.adSpend || 0) + plan.adSpend,
        directOrderLines: (current?.directOrderLines || 0) + plan.directOrderLines,
        directOrderAmount: (current?.directOrderAmount || 0) + plan.directOrderAmount,
        indirectOrderLines: (current?.indirectOrderLines || 0) + plan.indirectOrderLines,
        indirectOrderAmount: (current?.indirectOrderAmount || 0) + plan.indirectOrderAmount,
        totalOrderLines: (current?.totalOrderLines || 0) + plan.totalOrderLines,
        totalOrderAmount: (current?.totalOrderAmount || 0) + plan.totalOrderAmount,
      })
      byId.set(plan.campaignId, { campaignId: plan.campaignId, name: base.name, displayName: base.displayName, ...summed })
    }
  }
  const plans = Array.from(byId.values()).sort((left, right) => right.adSpend - left.adSpend || left.name.localeCompare(right.name, 'zh-CN'))
  const total = campaignSummary(plans.reduce((sum, plan) => ({
    impressions: sum.impressions + plan.impressions,
    clicks: sum.clicks + plan.clicks,
    adSpend: sum.adSpend + plan.adSpend,
    directOrderLines: sum.directOrderLines + plan.directOrderLines,
    directOrderAmount: sum.directOrderAmount + plan.directOrderAmount,
    indirectOrderLines: sum.indirectOrderLines + plan.indirectOrderLines,
    indirectOrderAmount: sum.indirectOrderAmount + plan.indirectOrderAmount,
    totalOrderLines: sum.totalOrderLines + plan.totalOrderLines,
    totalOrderAmount: sum.totalOrderAmount + plan.totalOrderAmount,
  }), { impressions: 0, clicks: 0, adSpend: 0, directOrderLines: 0, directOrderAmount: 0, indirectOrderLines: 0, indirectOrderAmount: 0, totalOrderLines: 0, totalOrderAmount: 0 }))
  return { plans, total }
}

function emptyPlanSummary(name: string, displayName: string): JdAdvertisingOrderPlanSummary {
  return { name, displayName, attributedOrderCount: 0, paidOrderCount: 0, cancelledOrderCount: 0, unpaidOrderCount: 0, skuCount: 0, rowCount: 0, paidRowCount: 0, paidSkuQuantity: 0, paidAmount: 0, directPaidOrderCount: 0, directPaidAmount: 0, indirectPaidOrderCount: 0, indirectPaidAmount: 0 }
}

const planSumKeys: (keyof JdAdvertisingOrderPlanSummary)[] = [
  'attributedOrderCount', 'paidOrderCount', 'cancelledOrderCount', 'unpaidOrderCount', 'rowCount', 'paidRowCount',
  'paidSkuQuantity', 'paidAmount', 'directPaidOrderCount', 'directPaidAmount', 'indirectPaidOrderCount', 'indirectPaidAmount',
]

function addPlanSummary(target: JdAdvertisingOrderPlanSummary, source: JdAdvertisingOrderPlanSummary) {
  for (const key of planSumKeys) (target[key] as number) += source[key] as number
  target.paidAmount = money(target.paidAmount)
  target.directPaidAmount = money(target.directPaidAmount)
  target.indirectPaidAmount = money(target.indirectPaidAmount)
}

function aggregateOrders(records: JdAdvertisingOrderDailyRecord[]) {
  const planMap = new Map<string, JdAdvertisingOrderPlanSummary>()
  const skuMap = new Map<string, JdAdvertisingOrderSkuSummary>()
  for (const record of records) {
    for (const plan of record.planSummaries) {
      const target = planMap.get(plan.name) || emptyPlanSummary(plan.name, plan.displayName)
      addPlanSummary(target, plan)
      planMap.set(plan.name, target)
    }
    for (const sku of record.skuSummaries) {
      const target = skuMap.get(sku.skuId) || { ...sku, rowCount: 0, attributedOrderCount: 0, paidOrderCount: 0, paidSkuQuantity: 0, paidAmount: 0, paidCouponAmount: 0, cancelledOrderCount: 0, unpaidOrderCount: 0, directPaidOrderCount: 0, directPaidAmount: 0, indirectPaidOrderCount: 0, indirectPaidAmount: 0, plans: [] }
      for (const key of ['rowCount', 'attributedOrderCount', 'paidOrderCount', 'paidSkuQuantity', 'paidAmount', 'paidCouponAmount', 'cancelledOrderCount', 'unpaidOrderCount', 'directPaidOrderCount', 'directPaidAmount', 'indirectPaidOrderCount', 'indirectPaidAmount'] as const) target[key] += sku[key]
      target.paidAmount = money(target.paidAmount)
      target.paidCouponAmount = money(target.paidCouponAmount)
      target.directPaidAmount = money(target.directPaidAmount)
      target.indirectPaidAmount = money(target.indirectPaidAmount)
      const nested = new Map(target.plans.map((plan) => [plan.name, plan]))
      for (const plan of sku.plans) {
        const current = nested.get(plan.name) || emptyPlanSummary(plan.name, plan.displayName)
        addPlanSummary(current, plan)
        nested.set(plan.name, current)
      }
      target.plans = Array.from(nested.values()).sort((left, right) => right.paidAmount - left.paidAmount)
      skuMap.set(sku.skuId, target)
    }
  }
  for (const plan of planMap.values()) plan.skuCount = Array.from(skuMap.values()).filter((sku) => sku.plans.some((item) => item.name === plan.name)).length
  return {
    paidOrderCount: records.reduce((sum, record) => sum + record.paidOrderCount, 0),
    paidSkuQuantity: records.reduce((sum, record) => sum + record.paidSkuQuantity, 0),
    paidAmount: money(records.reduce((sum, record) => sum + record.paidAmount, 0)),
    planSummaries: Array.from(planMap.values()),
    skuSummaries: Array.from(skuMap.values()).sort((left, right) => right.paidAmount - left.paidAmount || left.skuId.localeCompare(right.skuId)),
  }
}

function productName(product: ProductRecord | null, companyName: string) {
  return companyName || product?.name || product?.sourceName || '未识别商品'
}

function isoWeek(date: Date) {
  const value = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  value.setUTCDate(value.getUTCDate() + 4 - (value.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1))
  return Math.ceil((((value.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}

function CalendarMonth({ anchor, latestDate, draft, onPick, side, onMove }: {
  anchor: Date
  latestDate: string
  draft: { start: string; end: string }
  onPick: (date: string) => void
  side: 'left' | 'right'
  onMove: (months: number) => void
}) {
  const year = anchor.getFullYear()
  const month = anchor.getMonth()
  const first = new Date(year, month, 1)
  const offset = (first.getDay() + 6) % 7
  const dayCount = new Date(year, month + 1, 0).getDate()
  const rowCount = Math.ceil((offset + dayCount) / 7)
  const rows = Array.from({ length: rowCount }, (_, row) => {
    const rowStart = new Date(year, month, 1 - offset + row * 7)
    return { week: isoWeek(rowStart), dates: Array.from({ length: 7 }, (_, column) => new Date(year, month, 1 - offset + row * 7 + column)) }
  })
  return (
    <section className="ad-calendar-month">
      <header className="ad-calendar-month-head">
        {side === 'left' ? <div className="ad-calendar-nav"><button type="button" aria-label="上一年" onClick={() => onMove(-12)}><ChevronsLeft /></button><button type="button" aria-label="上个月" onClick={() => onMove(-1)}><ChevronLeft /></button></div> : <span />}
        <strong>{year}年{String(month + 1).padStart(2, '0')}月</strong>
        {side === 'right' ? <div className="ad-calendar-nav is-end"><button type="button" aria-label="下个月" onClick={() => onMove(1)}><ChevronRight /></button><button type="button" aria-label="下一年" onClick={() => onMove(12)}><ChevronsRight /></button></div> : <span />}
      </header>
      <div className="ad-calendar-grid"><span />{['一', '二', '三', '四', '五', '六', '日'].map((day) => <span className="ad-calendar-weekday" key={day}>{day}</span>)}
        {rows.flatMap((row) => [<span className="ad-calendar-week" key={`w${year}-${month}-${row.week}`}>{row.week}</span>, ...row.dates.map((date) => {
          if (date.getMonth() !== month) return <span className="ad-calendar-empty" key={date.toISOString()} />
          const value = isoDate(date)
          const selected = value === draft.start || value === draft.end
          const inRange = value >= draft.start && value <= draft.end
          return <button type="button" key={value} disabled={value > latestDate} onClick={() => onPick(value)} className={`ad-calendar-day${inRange ? ' is-range' : ''}${value === draft.start ? ' is-start' : ''}${value === draft.end ? ' is-end' : ''}${selected ? ' is-selected' : ''}`}>{date.getDate()}</button>
        })])}
      </div>
    </section>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  return <svg className="ad-sort-icon" viewBox="0 0 10 13" aria-hidden="true"><path className={active && direction === 'asc' ? 'is-active' : ''} d="m2 5 3-3 3 3" /><path className={active && direction === 'desc' ? 'is-active' : ''} d="m2 8 3 3 3-3" /></svg>
}

export function JdAdvertisingOrderDetailPage() {
  const { data, internalProductInfo, jdAdvertisingOrders } = useProductData()
  const latestDate = jdAdvertisingOrders?.dates.at(-1) || ''
  const [range, setRange] = useState<DateRange>(() => latestDate ? presetRange('yesterday', latestDate) : { key: 'yesterday', start: '', end: '' })
  const [selectedCampaignId, setSelectedCampaignId] = useState('overview')
  const [search, setSearch] = useState('')
  const [mapping, setMapping] = useState<MappingFilter>('all')
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'spend', direction: 'desc' })
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarAnchor, setCalendarAnchor] = useState(new Date())
  const [draftRange, setDraftRange] = useState({ start: '', end: '' })
  const [draftStart, setDraftStart] = useState<string | null>(null)
  const [selectedSku, setSelectedSku] = useState<string | null>(null)
  const [orderDetails, setOrderDetails] = useState<JdAdvertisingOrderDetailResponse | null>(null)
  const [detailError, setDetailError] = useState('')
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [copyToast, setCopyToast] = useState('')
  const calendarRef = useRef<HTMLDivElement>(null)
  const effectiveRange = range.start || !latestDate
    ? range
    : presetRange(range.key === 'custom' ? 'yesterday' : range.key, latestDate)

  useEffect(() => {
    if (!latestDate || range.start) return
    setRange(presetRange('yesterday', latestDate))
  }, [latestDate, range.start])

  useEffect(() => {
    if (!calendarOpen) return
    const close = (event: MouseEvent) => { if (!calendarRef.current?.contains(event.target as Node)) setCalendarOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [calendarOpen])

  useEffect(() => {
    if (!selectedSku) return
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setSelectedSku(null) }
    document.addEventListener('keydown', close)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', close); document.body.style.overflow = '' }
  }, [selectedSku])

  const records = useMemo(() => (jdAdvertisingOrders?.dailyRecords || []).filter((record) => record.clickDate >= effectiveRange.start && record.clickDate <= effectiveRange.end), [effectiveRange.end, effectiveRange.start, jdAdvertisingOrders])
  const campaigns = useMemo(() => aggregateCampaigns(records), [records])
  const orderAggregate = useMemo(() => aggregateOrders(records), [records])
  const selectedCampaign = campaigns.plans.find((plan) => plan.campaignId === selectedCampaignId) || null
  const selectedPlanName = selectedCampaign?.name || null
  const productsBySku = useMemo(() => new Map((data?.products || []).filter((product) => product.level === 'sku' && product.jdSkuId).map((product) => [product.jdSkuId, product])), [data])
  const companyNamesByCode = useMemo(() => new Map((internalProductInfo?.records || []).filter((record) => record.productCode && record.productName).map((record) => [record.productCode, record.productName])), [internalProductInfo])
  const rows = useMemo<AdvertisingSkuRow[]>(() => orderAggregate.skuSummaries.map((summary) => {
    const product = productsBySku.get(summary.skuId) || null
    const productCode = product?.merchantSku?.trim() || ''
    return { summary, product, productCode, productName: productName(product, companyNamesByCode.get(productCode) || ''), mapped: Boolean(productCode) }
  }), [companyNamesByCode, orderAggregate.skuSummaries, productsBySku])
  const planRows = useMemo(() => selectedPlanName ? rows.filter((row) => row.summary.plans.some((plan) => plan.name === selectedPlanName)) : rows, [rows, selectedPlanName])
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return planRows.filter((row) => {
      if (mapping === 'mapped' && !row.mapped) return false
      if (mapping === 'unmapped' && row.mapped) return false
      return !needle || `${row.productName} ${row.productCode} ${row.summary.skuId}`.toLowerCase().includes(needle)
    })
  }, [mapping, planRows, search])
  const activeRow = rows.find((row) => row.summary.skuId === selectedSku) || null
  const activeScopeSummary = activeRow && selectedPlanName ? activeRow.summary.plans.find((plan) => plan.name === selectedPlanName) || null : activeRow?.summary || null
  const selectedOrderPlan = selectedPlanName ? orderAggregate.planSummaries.find((plan) => plan.name === selectedPlanName) || null : null
  const metricCampaign = selectedCampaign || campaigns.total
  const metricOrders = selectedOrderPlan || orderAggregate
  const mappedCount = planRows.filter((row) => row.mapped).length
  const coverageText = records.length === 1 ? `已覆盖 ${records[0].clickDate}` : `已覆盖 ${records.length} 个数据日`

  useEffect(() => {
    if (!activeRow || !selectedSku) { setOrderDetails(null); setDetailError(''); return }
    const controller = new AbortController()
    if (spanDays(effectiveRange.start, effectiveRange.end) > 89) {
      setOrderDetails(null)
      setDetailsLoading(false)
      setDetailError('日期区间必须为1至90天')
      return
    }
    const params = new URLSearchParams({ start: effectiveRange.start, end: effectiveRange.end, sku: selectedSku })
    if (selectedPlanName) params.set('plan', selectedPlanName)
    setDetailsLoading(true)
    setDetailError('')
    fetch(`/api/jd/advertising-orders/details?${params}`, { signal: controller.signal, credentials: 'same-origin' })
      .then(async (response) => {
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.error || '订单明细加载失败')
        return payload as JdAdvertisingOrderDetailResponse
      })
      .then(setOrderDetails)
      .catch((error: Error) => { if (error.name !== 'AbortError') setDetailError(error.message) })
      .finally(() => { if (!controller.signal.aborted) setDetailsLoading(false) })
    return () => controller.abort()
  }, [activeRow, effectiveRange.end, effectiveRange.start, selectedPlanName, selectedSku])

  const selectRange = (key: Exclude<RangeKey, 'custom'>) => {
    if (!latestDate) return
    setRange(presetRange(key, latestDate))
    setSelectedCampaignId('overview')
    setSelectedSku(null)
    setCalendarOpen(false)
  }

  const openCalendar = () => {
    const draft = { start: range.start || latestDate, end: range.end || latestDate }
    setDraftRange(draft)
    setDraftStart(null)
    const first = parseDate(draft.start)
    first.setDate(1)
    if (draft.start.slice(0, 7) === draft.end.slice(0, 7)) first.setMonth(first.getMonth() - 1)
    setCalendarAnchor(first)
    setCalendarOpen(true)
  }

  const pickCalendarDay = (value: string) => {
    if (!draftStart) {
      setDraftStart(value)
      setDraftRange({ start: value, end: value })
    } else {
      setDraftRange(value < draftStart ? { start: value, end: draftStart } : { start: draftStart, end: value })
      setDraftStart(null)
    }
  }

  const applyCustomRange = () => {
    if (!draftRange.start || !draftRange.end) return
    if (spanDays(draftRange.start, draftRange.end) > 89) return
    setRange({ key: 'custom', ...draftRange })
    setSelectedCampaignId('overview')
    setSelectedSku(null)
    setCalendarOpen(false)
  }

  const sortValue = (plan: JdAdvertisingCampaignPlanPerformance, key: SortKey) => ({
    spend: plan.adSpend,
    spendShare: ratio(plan.adSpend, campaigns.total.adSpend) * 100,
    amount: plan.totalOrderAmount,
    amountShare: ratio(plan.totalOrderAmount, campaigns.total.totalOrderAmount) * 100,
    roi: plan.roi,
    cpc: plan.averageClickCost,
    conversion: ratio(plan.totalOrderLines, plan.clicks) * 100,
    orderCost: plan.averageOrderCost,
  })[key]
  const sortedCampaigns = [...campaigns.plans].sort((left, right) => {
    const first = sortValue(left, sort.key)
    const second = sortValue(right, sort.key)
    if (first == null) return 1
    if (second == null) return -1
    return sort.direction === 'asc' ? first - second : second - first
  })
  const updateSort = (key: SortKey) => setSort((current) => ({ key, direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc' }))

  const diagnosis = (plan: JdAdvertisingCampaignPlanPerformance) => {
    const spendShare = ratio(plan.adSpend, campaigns.total.adSpend)
    const amountShare = ratio(plan.totalOrderAmount, campaigns.total.totalOrderAmount)
    const conversion = ratio(plan.totalOrderLines, plan.clicks)
    const totalConversion = ratio(campaigns.total.totalOrderLines, campaigns.total.clicks)
    if (plan.adSpend > 0 && !plan.totalOrderLines) return { label: '有消耗无成交', tone: 'negative' }
    if (plan.averageOrderCost != null && campaigns.total.averageOrderCost != null && plan.averageOrderCost > campaigns.total.averageOrderCost * 2) return { label: '订单成本偏高', tone: 'warning' }
    if (amountShare > spendShare * 1.35) return { label: '成交贡献高于消耗', tone: 'positive' }
    if (amountShare < spendShare * .65) return { label: '成交贡献低于消耗', tone: 'warning' }
    if (conversion > totalConversion * 1.6 && plan.averageClickCost > campaigns.total.averageClickCost) return { label: '流量贵但转化好', tone: 'positive' }
    return { label: '稳定观察', tone: 'neutral' }
  }

  const insightPlans = useMemo(() => {
    const contribution = [...campaigns.plans].sort((left, right) => (ratio(right.totalOrderAmount, campaigns.total.totalOrderAmount) - ratio(right.adSpend, campaigns.total.adSpend)) - (ratio(left.totalOrderAmount, campaigns.total.totalOrderAmount) - ratio(left.adSpend, campaigns.total.adSpend)))[0]
    const zero = [...campaigns.plans].filter((plan) => plan.adSpend > 0 && !plan.totalOrderLines).sort((left, right) => right.adSpend - left.adSpend)[0]
    const cost = [...campaigns.plans].filter((plan) => plan.averageOrderCost != null).sort((left, right) => (right.averageOrderCost || 0) - (left.averageOrderCost || 0))[0]
    return { contribution, zero, cost }
  }, [campaigns])

  const choosePlan = (campaignId: string) => { setSelectedCampaignId(campaignId); setSelectedSku(null) }
  const copyOrderId = async (value: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(value)
      else {
        const helper = document.createElement('textarea')
        helper.value = value
        helper.setAttribute('readonly', '')
        helper.style.position = 'fixed'
        helper.style.opacity = '0'
        document.body.appendChild(helper)
        helper.select()
        document.execCommand('copy')
        helper.remove()
      }
      setCopyToast('订单号已复制')
      window.setTimeout(() => setCopyToast(''), 1300)
    } catch {
      setCopyToast('复制失败，请重试')
      window.setTimeout(() => setCopyToast(''), 1800)
    }
  }

  if (!jdAdvertisingOrders) return <div className="loading-state">正在加载广告效果数据…</div>
  if (!latestDate) return <section className="ad-analysis-empty is-page"><strong>暂无广告订单数据</strong><span>导入京准通订单明细后再查看本页。</span></section>
  const rightMonth = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + 1, 1)
  const drawerOrders = orderDetails?.orders || []
  const drawerQuantity = drawerOrders.reduce((sum, order) => sum + order.skuQuantity, 0)
  const drawerAmount = money(drawerOrders.reduce((sum, order) => sum + order.skuAmount, 0))

  return (
    <div className="ad-analysis-page page-stack">
      <section className="ad-analysis-scope-row">
        <div className="ad-analysis-scope"><span>统计区间 {rangeLabel(effectiveRange)}</span><span>{coverageText}</span><span>{jdAdvertisingOrders.attribution} · 成交订单</span></div>
        <div className="ad-date-toolbar" ref={calendarRef}>
          <div className="ad-range-tabs" aria-label="日期筛选">
            {([['yesterday', '昨天'], ['last7', '近7天'], ['last30', '近30天'], ['day', '日'], ['week', '周'], ['month', '月']] as const).map(([key, label]) => <button type="button" className={`ad-range-tab${range.key === key ? ' is-active' : ''}`} onClick={() => selectRange(key)} key={key}>{label}</button>)}
            <button type="button" className={`ad-range-tab${range.key === 'custom' || calendarOpen ? ' is-active' : ''}`} onClick={openCalendar}>自定义</button>
          </div>
          {calendarOpen ? <div className="ad-custom-calendar">
            <div className="ad-calendar-layout">
              <CalendarMonth anchor={calendarAnchor} latestDate={latestDate} draft={draftRange} onPick={pickCalendarDay} side="left" onMove={(months) => setCalendarAnchor(new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + months, 1))} />
              <CalendarMonth anchor={rightMonth} latestDate={latestDate} draft={draftRange} onPick={pickCalendarDay} side="right" onMove={(months) => setCalendarAnchor(new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + months, 1))} />
            </div>
            <footer><span>{spanDays(draftRange.start, draftRange.end) > 89 ? '明细最多查询90天，请缩小区间' : `已选择 ${rangeLabel(draftRange)}`}</span><div><button type="button" onClick={() => setCalendarOpen(false)}>取消</button><button type="button" className="is-confirm" onClick={applyCustomRange} disabled={!draftRange.start || spanDays(draftRange.start, draftRange.end) > 89}>确定</button></div></footer>
          </div> : null}
        </div>
      </section>

      {records.length && campaigns.plans.length ? <section className="ad-plan-rail-shell" aria-label="推广计划导航"><div className="ad-plan-rail">
        <button type="button" className={`ad-plan-tab${selectedCampaignId === 'overview' ? ' is-active' : ''}`} onClick={() => choosePlan('overview')}><b>总览</b><span>{currency.format(campaigns.total.adSpend)}</span><small>ROI {decimal.format(campaigns.total.roi)}</small></button>
        {campaigns.plans.map((plan) => <button type="button" key={plan.campaignId} title={plan.name} className={`ad-plan-tab${selectedCampaignId === plan.campaignId ? ' is-active' : ''}${plan.adSpend > 0 && !plan.totalOrderLines ? ' is-attention' : ''}`} onClick={() => choosePlan(plan.campaignId)}><b>{plan.displayName}</b><span>{currency.format(plan.adSpend)}</span><small>{plan.totalOrderLines ? `ROI ${decimal.format(plan.roi)}` : '无成交'}</small></button>)}
      </div></section> : null}

      {records.length && campaigns.plans.length ? <>
        <section className="ad-metric-band" aria-label="广告效果概览">
          <div><span>广告花费</span><strong>{currency.format(metricCampaign.adSpend)}</strong><small>{selectedCampaign?.displayName || '全部推广计划'}</small></div>
          <div><span>归因成交金额</span><strong>{currency.format(metricCampaign.totalOrderAmount)}</strong><small>30天点击 · 不含赠品</small></div>
          <div><span>成交订单行</span><strong>{integer.format(metricCampaign.totalOrderLines)}</strong><small>{integer.format(metricOrders.paidOrderCount)} 个已付款订单（分日去重）</small></div>
          <div><span>投产比</span><strong>{decimal.format(metricCampaign.roi)}</strong><small>归因成交金额 ÷ 广告花费</small></div>
          <div><span>平均订单成本</span><strong>{metricCampaign.averageOrderCost == null ? '—' : currency.format(metricCampaign.averageOrderCost)}</strong><small>广告花费 ÷ 成交订单行</small></div>
          <div><span>产品映射</span><strong>{mappedCount}<em>/ {planRows.length}</em></strong><small>{planRows.length - mappedCount}个SKU待补公司编码</small></div>
        </section>

        {selectedCampaign ? <section className="ad-detail-grid">
          <article className="ad-panel"><header className="ad-panel-head"><div><h2>投放漏斗</h2><p>{selectedCampaign.displayName} · 统计区间 {rangeLabel(effectiveRange)}</p></div><span>计划ID {selectedCampaign.campaignId}</span></header><div className="ad-funnel">
            <div><span>展现</span><strong>{integer.format(selectedCampaign.impressions)}</strong><small>流量入口</small></div>
            <div><span>点击</span><strong>{integer.format(selectedCampaign.clicks)}</strong><small>CTR {decimal.format(selectedCampaign.clickThroughRate)}%</small></div>
            <div><span>成交订单行</span><strong>{integer.format(selectedCampaign.totalOrderLines)}</strong><small>转化率 {decimal.format(ratio(selectedCampaign.totalOrderLines, selectedCampaign.clicks) * 100)}%</small></div>
            <div><span>归因成交</span><strong>{currency.format(selectedCampaign.totalOrderAmount)}</strong><small>ROI {decimal.format(selectedCampaign.roi)}</small></div>
          </div></article>
          <aside className="ad-panel ad-structure"><h2>成交结构</h2>{[
            ['直接成交', selectedCampaign.directOrderAmount], ['间接成交', selectedCampaign.indirectOrderAmount],
          ].map(([label, amount]) => { const share = ratio(Number(amount), selectedCampaign.totalOrderAmount) * 100; return <div className="ad-structure-row" key={label}><div><span>{label}</span><strong>{currency.format(Number(amount))} · {share.toFixed(1)}%</strong></div><i><em style={{ width: `${share}%` }} /></i></div> })}
            <p className={selectedCampaign.totalOrderLines ? '' : 'is-warning'}>{selectedCampaign.totalOrderLines ? `${selectedCampaign.displayName}当前CPC为${currency.format(selectedCampaign.averageClickCost)}、点击转化率${decimal.format(ratio(selectedCampaign.totalOrderLines, selectedCampaign.clicks) * 100)}%、订单成本${currency.format(selectedCampaign.averageOrderCost || 0)}。` : `该计划已花费${currency.format(selectedCampaign.adSpend)}，当前尚无归因成交；30天点击归因仍可能回补，本页只标记关注。`}</p>
          </aside>
        </section> : <section className="ad-overview-grid">
          <article className="ad-panel"><header className="ad-panel-head"><div><h2>计划效率对比</h2><p>点击任一计划行进入计划详情</p></div><span>按{sortLabels[sort.key]}从{sort.direction === 'desc' ? '高到低' : '低到高'}</span></header>
            <table className="ad-efficiency-table"><thead><tr><th>计划</th>{(Object.keys(sortLabels) as SortKey[]).map((key) => <th className="is-sortable" aria-sort={sort.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'} key={key}><button type="button" className={sort.key === key ? 'is-active' : ''} onClick={() => updateSort(key)}>{sortLabels[key]}<SortIcon active={sort.key === key} direction={sort.direction} /></button></th>)}<th>诊断</th></tr></thead><tbody>
              {sortedCampaigns.map((plan) => { const result = diagnosis(plan); return <tr key={plan.campaignId} onClick={() => choosePlan(plan.campaignId)}><td className="ad-plan-name">{plan.displayName}</td><td>{plan.adSpend.toFixed(2)}</td><td>{(ratio(plan.adSpend, campaigns.total.adSpend) * 100).toFixed(1)}%</td><td>{plan.totalOrderAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</td><td>{(ratio(plan.totalOrderAmount, campaigns.total.totalOrderAmount) * 100).toFixed(1)}%</td><td>{plan.roi.toFixed(2)}</td><td>{plan.averageClickCost.toFixed(2)}</td><td>{(ratio(plan.totalOrderLines, plan.clicks) * 100).toFixed(2)}%</td><td>{plan.averageOrderCost == null ? '—' : plan.averageOrderCost.toFixed(2)}</td><td className={`is-${result.tone}`}>{result.label}</td></tr> })}
            </tbody></table><div className="ad-table-note">数据口径：30天点击 · 不含赠品 · 成交订单；单日判断仅用于关注，不直接形成调预算或停投动作。</div>
          </article>
          <aside className="ad-panel ad-insights"><h2>关键发现</h2><div>
            {insightPlans.contribution ? <p><b>1</b><span>{insightPlans.contribution.displayName}消耗<strong>{(ratio(insightPlans.contribution.adSpend, campaigns.total.adSpend) * 100).toFixed(1)}%</strong>，贡献<strong>{(ratio(insightPlans.contribution.totalOrderAmount, campaigns.total.totalOrderAmount) * 100).toFixed(1)}%</strong>成交。</span></p> : null}
            {insightPlans.zero ? <p className="is-warning"><b>2</b><span>{insightPlans.zero.displayName}花费<strong>{currency.format(insightPlans.zero.adSpend)}</strong>，当前成交<strong>0</strong>。</span></p> : null}
            {insightPlans.cost ? <p className="is-warning"><b>3</b><span>{insightPlans.cost.displayName}订单成本<strong>{currency.format(insightPlans.cost.averageOrderCost || 0)}</strong>，为当前最高。</span></p> : null}
          </div></aside>
        </section>}

        <section className="ad-panel ad-sku-panel"><header className="ad-panel-head"><div><h2>{selectedCampaign ? `${selectedCampaign.displayName} · 按SKU汇总` : '按SKU汇总'}</h2><p>{selectedCampaign ? '只展示当前计划归因商品；计划花费不分摊到SKU。' : '选择具体计划后，下方将展示计划漏斗、直接/间接订单结构及SKU明细。'}</p></div><span>显示 {visibleRows.length} / {planRows.length} 个SKU</span></header>
          <div className="ad-sku-toolbar"><label><Search aria-hidden="true" /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索产品名称、公司编码或京东SKU" /></label><select aria-label="产品映射状态" value={mapping} onChange={(event) => setMapping(event.target.value as MappingFilter)}><option value="all">全部映射状态</option><option value="mapped">已匹配公司产品</option><option value="unmapped">待补公司编码</option></select></div>
          {visibleRows.length ? <table className="ad-sku-table"><thead><tr><th>产品</th><th>公司产品编码</th><th>京东SKU ID</th><th>已付款订单</th><th>件数</th><th>已付款金额</th></tr></thead><tbody>{visibleRows.map((row) => { const scope = selectedPlanName ? row.summary.plans.find((plan) => plan.name === selectedPlanName) || row.summary : row.summary; return <tr key={row.summary.skuId}><td><div className="ad-sku-product">{row.product?.image ? <img src={row.product.image} alt="" /> : <span>无图</span>}<div><strong>{row.productName}</strong><small>{row.product?.sourceName && row.product.sourceName !== row.productName ? row.product.sourceName : '京东广告成交商品'}</small></div></div></td><td>{row.productCode || <em>待补映射</em>}</td><td><button type="button" className="ad-sku-link" onClick={() => setSelectedSku(row.summary.skuId)}>{row.summary.skuId}<ChevronRight /></button></td><td>{integer.format(scope.paidOrderCount)}</td><td>{integer.format(scope.paidSkuQuantity)}</td><td>{currency.format(scope.paidAmount)}</td></tr> })}</tbody></table> : <div className="ad-analysis-empty"><strong>{selectedCampaign && !planRows.length ? '该计划暂无归因SKU' : '没有匹配的SKU'}</strong><span>{selectedCampaign && !planRows.length ? '有消耗无成交，SKU明细保持为空。' : '请调整搜索词或映射状态。'}</span></div>}
        </section>
      </> : <section className="ad-analysis-empty is-page"><strong>{rangeLabel(effectiveRange)} 暂无可联动的广告数据</strong><span>已导入日期：{jdAdvertisingOrders.dates.join('、')}。页面不会用其他日期数据补0。</span></section>}

      {activeRow && activeScopeSummary ? <div className="ad-order-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedSku(null) }}><aside className="ad-order-detail-drawer" role="dialog" aria-modal="true" aria-labelledby="adOrderDrawerTitle">
        <header><div className="ad-drawer-product">{activeRow.product?.image ? <img src={activeRow.product.image} alt="" /> : <span>无图</span>}<div><h2 id="adOrderDrawerTitle">{activeRow.productName}</h2><p>公司编码 {activeRow.productCode || '待补映射'} · 京东SKU {activeRow.summary.skuId}</p></div></div><button type="button" aria-label="关闭订单明细" onClick={() => setSelectedSku(null)}><X /></button></header>
        <section className="ad-drawer-summary"><div><span>已付款订单</span><strong>{detailsLoading || detailError ? '—' : integer.format(drawerOrders.length)}</strong></div><div><span>商品件数</span><strong>{detailsLoading || detailError ? '—' : integer.format(drawerQuantity)}</strong></div><div><span>SKU金额</span><strong>{detailsLoading || detailError ? '—' : currency.format(drawerAmount)}</strong></div></section>
        <div className="ad-drawer-body"><header><div><h3>具体订单列表</h3><span>{selectedCampaign ? `仅展示“${selectedCampaign.displayName}”计划归因订单` : '全部推广计划归因订单'}</span></div><span>点击日期 {rangeLabel(effectiveRange)} · 30天点击归因</span></header>
          {detailsLoading ? <div className="ad-drawer-message">正在加载订单明细…</div> : detailError ? <div className="ad-drawer-message is-error">{detailError}</div> : drawerOrders.length ? <div className="ad-order-list"><div className="ad-order-grid-head"><span>订单</span><span>时间</span><span>推广计划</span><span>地区</span><span>件数</span><span>金额</span></div>{drawerOrders.map((order) => <div className="ad-order-grid-row" key={`${order.orderId}-${order.skuId}-${order.campaignName}-${order.clickTime}`}><div><button type="button" className="ad-order-id" title="点击复制订单号" aria-label={`复制订单号 ${order.orderId}`} onClick={() => copyOrderId(order.orderId)}>{order.orderId}<Copy /></button><p><span>{order.orderType}</span><strong>{order.status}</strong></p></div><div className="ad-order-time"><span><em>下单</em>{order.orderTime.slice(5)}</span><span><em>点击</em>{order.clickTime.slice(5)}</span></div><div className="ad-order-plan-name" title={order.campaignName}>{order.campaignName}</div><div className="ad-order-region">{[order.province, order.city].filter(Boolean).join(' · ') || '—'}</div><div>{order.skuQuantity}</div><div>{currency.format(order.skuAmount)}</div></div>)}</div> : <div className="ad-drawer-message">当前筛选范围暂无已付款订单明细。</div>}
        </div><div className={`ad-copy-toast${copyToast ? ' is-show' : ''}`} role="status">{copyToast}</div>
      </aside></div> : null}
    </div>
  )
}
